<template>
  <teleport to="body">
    <overlay
      key="product-form-drawer"
      :focus-return="config.focusReturn"
      block-class="product-form-drawer"
      :direction="isMobile ? 'up' : 'left'"
      mobile-drag-close
      :overlay-label="config.strings.label ? config.strings.label : ''"
      namespace="product-form-drawer"
      show-close
      :type="isMobile ? 'shelf-modal' : 'drawer'"
    >
      <template
        v-if="!isMobile"
        #title
      >
        <div class="product-form-drawer__header-wrapper">
          <button
            v-if="product?.id"
            class="product-form-drawer__close-btn"
            type="button"
            @click="handleCloseOverlay('product-form-drawer')"
          >
            <icon-arrow-left />

            <span
              class="text-body-2xs-regular"
              v-text="config.strings.return"
            />
          </button>
        </div>
      </template>

      <template #body>
        <div
          v-if="isMobile && !showBackInStock"
          class="product-form-drawer__back-btn"
        >
          <button
            v-if="config.strings.back"
            class="product-form-drawer__close-btn"
            type="button"
            @click="handleCloseOverlay('product-form-drawer')"
          >
            <icon-arrow-left />

            <span
              class="text-body-2xs-regular"
              v-text="config.strings.back"
            />
          </button>
        </div>

        <div
          v-if="!isMobile || !showBackInStock"
          class="product-form-drawer__details"
        >
          <div
            :key="product?.id"
            class="product-form-drawer__images"
          >
            <product-card
              v-if="product?.id"
              card-layout="small"
              enable-image-carousel
              :enable-flat-image="false"
              enable-variant-image
              :flat-background-color="getBackgroundColor()"
              :load-secondary-image="false"
              padded-images
              :product="product"
              :product-card-location="{
                key: 'productFormDrawer',
              }"
              :show-badges="false"
              :show-product-content="false"
              :show-quick-add="false"
              :show-swatches="false"
              :show-wishlist="false"
              :image-background-color="getBackgroundColor()"
            />
          </div>

          <div
            :key="product"
            class="product-form-drawer__product-info"
          >
            <div class="product-form-drawer__product">
              <h2
                :id="`product-title-${product?.handle}`"
                class=" product-form-drawer__title text-body-2xs-regular"
                :class="{ 'font-family-freight': config?.location?.key === 'pdpShopTheLook' }"
                v-text="product?.title"
              />

              <product-prices
                :available="product?.available"
                :compare-at-price="product?.compareAtPrice?.amount"
                :price="product?.price?.amount"
                :varies="product?.price?.varies"
                class-price="text-body-2xs-regular"
                class-compare-at-price="text-body-2xs-regular"
              />

              <p
                class="text-body-2xs-regular"
                v-text="productLabel"
              />
            </div>

            <div class="product-form-drawer__bookmark_icon">
              <wishlist-button
                v-show="renderWishlistBtn"
                class="product-form-drawer__wishlist"
                :product="product || false"
                template="card"
                :variant="product?.selectedOrFirstAvailableVariant"
              />
            </div>
          </div>
        </div>

        <quick-add-to-cart
          v-if="product?.id"
          :key="product?.id"
          is-mobile-inline
          is-pdp-stl
          is-stl-type
          :number-of-sizes-per-row="6"
          :product="product"
          show-confirmation-button
          show-view-product-button
          size-guide-position="bottom"
          :show-cart-drawer="config.location !== 'customDrawer'"
          placement="customDrawer"
          :product-card-location="config.location"
        />
      </template>
    </overlay>
  </teleport>
</template>

<script>

/**
 * Vue: Product form drawer (product-form-drawer)
 * -----------------------------------------------------------------------------
 * A product form in a drawer for quick shop capabilities.
 *
 * @param {String} namespace - A unique namespace. "-modal" will be appended for
 * everything related to the overlay. The namespace is used in
 * `quick-add-to-cart` for back in stock settings.
 */

import { mapActions, mapState } from 'vuex'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'

import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductPrices from '~global/product-prices/product-prices'
import QuickAddToCart from '~async/quick-add-to-cart/quick-add-to-cart'

import WishlistButton from '~async/wishlist-button/wishlist-button'

export default {
  name: 'ProductFormDrawer',

  components: {
    IconArrowLeft,
    Overlay,
    ProductCard,
    ProductPrices,
    QuickAddToCart,
    WishlistButton,
  },

  data() {
    return {
      cnvsGlobal: true,
      showBackInStock: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      product: (state) => state.productFormDrawer.product,
      config: (state) => state.productFormDrawer.config,
    }),

    /**
     * Compute product label.
     * @returns {String}
     */
    productLabel() {
      if (!this.product) {
        return ''
      }

      const variant = this.product?.selectedOrFirstAvailableVariant

      const swatchOption = variant?.selectedOptions?.find((option) => {
        return this.$variable('settings.product.productSwatchOptionNames')?.includes(option.name?.toLowerCase())
      })

      if (!swatchOption) {
        return ''
      }

      return this.$string(`product.cta.${swatchOption.name?.toLowerCase()}_label`, {
        replace: {
          [swatchOption.name?.toLowerCase()]: swatchOption.value,
        },
      })
    },

    /**
     * Renders wishlist button in all cases
     */
    renderWishlistBtn() {
      return true
    },
  },

  mounted() {
    cnvs.EventBus.listen('productDrawer:customFocus', (customFocus) => {
      const focusReturnEl = customFocus ? document.querySelector(customFocus) : false

      if (focusReturnEl) {
        setTimeout(() => {
          focusReturnEl.focus({ preventScroll: true })
        }, this.$timing('slow'))
      }
    })
  },

  methods: {

    /**
     * Overlay actions
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
    }),

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleCloseOverlay(nameSpace) {
      this.closeOverlay(nameSpace)

      cnvs.EventBus.emit('productDrawer:customFocus', this.config.focusReturn)
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * returns product card background-color
     */
    getBackgroundColor() {
      if (this.product.cardBackground) {
        return this.product.cardBackground
      }

      return '--color-brand-light-gray'
    },
  },
}
</script>
